"use client"

import { createContext, useContext, useState, type ReactNode, Dispatch, SetStateAction } from "react"
import { CommissionTransaction } from "@/types/commission-dashboard"

interface SelectedTransactionsState {
  transactions: CommissionTransaction[]
  standardTotal: number
  permanentTotal: number
  selectedTotal: number
  isAdhoc: boolean
  statementDate: string
}

interface CommissionContextType {
  selectedTransactionsState: SelectedTransactionsState
  setSelectedTransactionsState: (state: SelectedTransactionsState) => void
  selectedCurrentIds: number[]
  setSelectedCurrentIds: Dispatch<SetStateAction<number[]>>
  selectedFutureIds: number[]
  setSelectedFutureIds: Dispatch<SetStateAction<number[]>>
}

// Create context
const CommissionContext = createContext<CommissionContextType | undefined>(undefined)

// Provider component
export function CommissionProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [selectedTransactionsState, setSelectedTransactionsState] = useState<SelectedTransactionsState>({
    transactions: [],
    standardTotal: 0,
    permanentTotal: 0,
    selectedTotal: 0,
    isAdhoc: false,
    statementDate: ''
  })
  const [selectedCurrentIds, setSelectedCurrentIds] = useState<number[]>([])
  const [selectedFutureIds, setSelectedFutureIds] = useState<number[]>([])

  return (
    <CommissionContext.Provider
      value={{
        selectedTransactionsState,
        setSelectedTransactionsState,
        selectedCurrentIds,
        setSelectedCurrentIds,
        selectedFutureIds,
        setSelectedFutureIds
      }}
    >
      {children}
    </CommissionContext.Provider>
  )
}

// Custom hook to use commission context
export function useCommission() {
  const context = useContext(CommissionContext)
  if (context === undefined) {
    throw new Error('useCommission must be used within a CommissionProvider')
  }
  return context
}
